<template>
	<div :class="['box', { 'is-highlighted': highlight }, 'score-panel']">
		<div class="score-header">
			<h3
				v-if="heading"
				class="title is-6 is-marginless"
				:class="{ 'column-tooltip': tooltip }"
			>
				{{ heading }}
				<tooltip
					v-if="tooltip"
					:class="['feature', { 'is-highlighted': highlight }]"
					icon="question-circle"
					library="fas"
					position="is-right"
					:tip="tooltip"
					size="is-medium"
				/>
			</h3>
			<p v-if="subtitle" class="is-size-6">{{ subtitle }}</p>
		</div>
		<div v-if="scoreData" class="score-data">
			<p v-if="validScore" class="is-size-3">
				<strong :style="`color: ${colour};`">{{ score }}</strong>
			</p>
			<p v-else>
				<span :style="`color: ${colour};`">{{ score }}</span>
			</p>
			<p
				v-if="!showChange"
				:class="['is-size-6', 'score-growth', growthColour]"
			>
				<dc-icon
					v-if="direction"
					:icon="`arrow-${direction}`"
					library="fas"
					no-margin
				/>
				<span v-text="`${change}%`" />
				<span v-if="growthLabel" v-text="growthLabel" />
			</p>
		</div>
		<div v-else class="score-data">
			<dc-icon v-if="isFetching" icon="sync" no-margin spin />
			<span v-text="label" />
		</div>
	</div>
</template>

<script>
import sourceMixin from "./sourceMixin";
import { countPlaces } from "@lab/utils/format";
import tooltip from "../../../components/ListingCard/components/Tooltip";
const noDataMessage = "There is no data to display";

export default {
	components: { tooltip },
	mixins: [sourceMixin],
	// props: {
	// 	data: {
	// 		type: Object,
	// 		default: () => null
	// 	},
	// 	alphabet: {
	// 		type: Array,
	// 		default: () => "abcdefghijklmnopqrstuvwxyz".split("")
	// 	},
	// 	source: {
	// 		type: String,
	// 		default: () => null
	// 	},
	// 	errorMessage: {
	// 		type: String,
	// 		default: () => noDataMessage
	// 	}
	// },
	data() {
		return {
			size: 356,
			scoreData: null
		};
	},
	computed: {
		validScore() {
			return this.scoreData.Label !== noDataMessage;
		},
		score() {
			return this.scoreData.Label;
			// return this.scoreData ? Math.round(this.scoreData.Percentage) : 0;
		},
		content() {
			return this.data.content || null;
		},
		highlight() {
			return this.data.highlight || false;
		},
		mainClass() {
			if (
				this.data !== null &&
				this.data.hideborder !== undefined &&
				this.data.hideborder === "yes"
			) {
				return "score-panel-no-border";
			}
			return "score-panel";
		},
		colour() {
			// If the panel is highlighted we don't want to assign a colour to text
			if (this.highlight) return null;

			// If colour is handed to the column and is a string hand out directly
			if (this.data.colour && !Array.isArray(this.data.colour)) {
				return this.data.colour;
			}

			let colours =
				// We can hand in an array of colours. If we choose to do so here.
				this.data.colour && Array.isArray(this.data.colour)
					? this.data.colour
					: ["#e53935", "#ffb300", "#43a047"];

			const { Type, Percentage } = this.scoreData;

			if (
				Type === "percentage" ||
				Type === "integerpercentage" ||
				Type === "floatpercentage"
			) {
				let index =
					Percentage >= 50 && Percentage < 75 ? 1 : Percentage >= 75 ? 2 : 0;
				return colours[index];
			}

			return "#363636";
		},
		change() {
			const change = this.scoreData.PercentageChange;
			const decimals = countPlaces(change) > 2 ? 2 : 0;
			return this.scoreData.PercentageChange.toFixed(decimals);
		},
		growthColour() {
			return this.change > 0
				? "has-text-success"
				: this.change < 0
				? "has-text-danger"
				: null;
		},
		direction() {
			return this.change > 0 ? "up" : this.change < 0 ? "down" : null;
		},
		heading() {
			return this.data.title;
		},
		subtitle() {
			return this.data.label;
		},
		tooltip() {
			return this.data.tip || null;
		},
		showChange() {
			return this.data.hidechange;
		},
		growthLabel() {
			return this.data.growthLabel || " change from preceding period";
		}
	},

	methods: {
		async fetchData() {
			this.isFetching = true;
			this.scoreData = null;
			this.label = "Loading";

			if (!this.isValid(this.source)) {
				this.isFetching = false;
				return;
			}

			await this.fetchSource(this.source).then(
				(response) => {
					this.scoreData = response;
					this.isFetching = false;
					if (!response) {
						this.label = this.errorMessage;
					}
				},
				() => {
					this.label = this.errorMessage;
					this.isFetching = false;
				}
			);
		},
		ScoreAvatar(name, size) {
			name = name || "0";
			size = size || this.size;

			if (window.devicePixelRatio) {
				size = size * window.devicePixelRatio;
			}
			let index =
				this.score > 49 && this.score < 70 ? 1 : this.score > 70 ? 2 : 0;

			let canvas = document.createElement("canvas");
			canvas.width = size;
			canvas.height = size;

			let context = canvas.getContext("2d");
			context.fillStyle =
				this.colours.length > 1 ? this.colours[index] : this.colours[0];
			context.fillRect(0, 0, canvas.width, canvas.height);
			context.font = Math.round(canvas.width / 2) + "px proxima-nova";
			context.textAlign = "center";
			context.fillStyle = "#FFF";
			context.fillText(name, size / 2, size / 1.5);

			const dataURI = canvas.toDataURL();
			canvas = null;

			return dataURI;
		}
	}
};
</script>
<style lang="scss" scoped>
.score-panel {
	padding-bottom: 0.625rem;
	position: relative;
	&.is-highlighted {
		background-color: #484858;
		h3.title,
		p,
		p > strong {
			color: #fff;
		}
	}
	display: flex;
	flex-direction: column;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-content: stretch;
	align-items: flex-start;
}
.score-panel-no-border {
	position: relative;
	padding-right: 10px;
}
.column-tooltip {
	padding-right: 1em;
}
.tooltip {
	position: absolute;
	right: 0;
	top: 0;
}
.is-dashed {
	border-bottom: none;
}
.score-header {
	order: 0;
	flex: 1 1 auto;
	align-self: stretch;
}
.score-data {
	margin-top: 0.5rem;
	line-height: 1;
	.score-growth {
		margin-top: 0.5rem;
	}
}
</style>
